<template>
  <div class="container my-5">

    <div class="card">

      <div class="card-header">
        <h4 class="display-4">{{ initialTitle }}</h4>
      </div>

      <div class="card-body">
        <form @submit.prevent="handleSubmit" class="m-3" :class="{ 'was-validated': wasValidated }" novalidate>

          <!-- Title -->
          <EventDataFormGroup
            name="Title" v-model.trim="title"
            :validation="validation.title"
          >
            <template #note>Titles should be short and descriptive (e.g. &quot;5k In The Heights&quot;)</template>
          </EventDataFormGroup>

          <!-- Description -->
          <QuillFormRow
            ref="quill"
            :text="description"
            :html="htmlDescription"
            :wasValidated="wasValidated"
            @text="setDescription"
            @html="setHtmlDescription"/>

          <!-- Date and Times -->
          <div class="form-row">
            <EventDataFormGroup
              name="Date" type="date" classes="col"
              :validation="validation.date"
              v-model.trim="dateString"
            />

            <EventDataFormGroup
              name="Start Time" type="time" classes="col"
              :validation="validation.startTime"
              v-model.trim="startTimeString"
            />

            <EventDataFormGroup
              name="End Time" type="time" classes="col"
              :validation="validation.endTime"
              v-model.trim="endTimeString"
            />
          </div>

          <!-- Venue -->
          <EventDataFormGroup
            name="Venue" placeholder="Diane's Spin Gym"
            :validation="validation.venue"
            v-model.trim="venue"
          />

          <!-- Address -->
          <div class="form-row">

            <EventDataFormGroup name="Address" classes="col-6" placeholder="1234 Main St" v-model.trim="address"/>

            <EventDataFormGroup name="City" classes="col" placeholder="Houston" disabled v-model.trim="city"/>

            <EventDataFormGroup name="Zip" classes="col" placeholder="77005" pattern="[0-9]{5}" v-model.trim="zipCode"/>

          </div>

          <!-- Price and Capacity -->
          <div class="form-row">

            <div class="form-group pr-2 col-lg-6 col-md-8 ">

              <template v-if="stripeAccount">
                <label for="price">{{ isDonationBased ? 'Minimum Donation' : 'Price' }}</label>

                <div class="input-group">
                  <span class="input-group-addon">$</span>
                  <input type="number" id="price" name="price" class="form-control text-right"
                         min="0" placeholder="0.00"
                         v-model.number="price" :disabled="isFree">
                </div>

                <div class="mt-2 mx-1 d-flex justify-content-between">

                  <div>
                    <input type="checkbox" title="free" id="free" name="free" class="mr-2" v-model="isFree">This event is free
                  </div>

                  <div class="ml-3">
                    <input type="checkbox" title="donation" id="donation" name="donation" class="mr-2" v-model="isDonationBased">This event is donation-based
                  </div>

                </div>
              </template>

              <template v-else>
                <label for="price">Price</label>
                <router-link to="/payments">
                  <h5>Complete sign up to create paid events</h5>
                </router-link>
              </template>

            </div>

            <div class="form-group pl-2 col">
              <label for="capacity">Capacity</label>
              <div class="input-group">
                <input type="number" id="capacity" name="capacity" class="form-control text-right" min="0" placeholder="0"
                       v-model.number="capacity" :disabled="noCapacity">
              </div>

              <div class="mt-2 mx-1 d-flex justify-content-end align-items-center">
                <input type="checkbox" v-model="noCapacity" class="mr-2">N/A
              </div>

            </div>

          </div>

          <!-- Image -->
          <CroppieFormRow v-model="imageSource" :invalid="wasValidated && imageSource.size === 0"/>

          <!-- Tags -->
          <div class="form-group">
            <div class="d-flex flex-column mb-2">
              <label for="tags" class="mb-0">Tags</label>
              <small>(Cmd+click for multiple selection)</small>
            </div>
            <select multiple class="form-control mb-5" id="tags" ref="tags" name="tags" @change="setTags" required>
              <option value="walkingJogging">Walking/Jogging</option>
              <option value="running">Running</option>
              <option value="flowYoga">Flow Yoga</option>
              <option value="restorativeYoga">Restorative Yoga</option>
              <option value="pilates">Pilates</option>
              <option value="rockClimbing">Rock Climbing</option>
              <option value="hiit">HIIT</option>
              <option value="spin">Spin</option>
              <option value="outdoorCycling">Outdoor Cycling</option>
              <option value="otherStudio">Other Studio Classes</option>
            </select>
          </div>

          <input type="submit" class="btn btn-primary" :value="isNew ? 'Create Event' : 'Save Changes' "/>
        </form>
      </div>

    </div>

  </div>
</template>

<script>
import EventDataFormGroup from '../components/EventDataFormGroup'
import QuillFormRow from '../components/EventDataFormQuillRow'
import CroppieFormRow from './EventDataFormCroppieRow'
import { postEvent } from '../mixins/events'
import { Store } from '../store'

export default {
  components: {
    EventDataFormGroup: EventDataFormGroup,
    QuillFormRow: QuillFormRow,
    CroppieFormRow: CroppieFormRow
  },
  mixins: [
    {
      methods: {
        today: today,
        earliestTime: earliestTime,
        postEvent: postEvent
      }
    }
  ],
  props: {
    prefill: {
      type: Object,
      default: () => {}
    },
    isNew: {
      type: Boolean,
      required: true
    }
  },
  data () {
    return {
      wasValidated: false,
      initialTitle: '',
      stripeAccount: Store.host.stripeAccount,

      id: '',
      title: '',
      description: '',
      htmlDescription: '',
      venue: '',
      address: '',
      city: 'Houston',
      zipCode: '',
      price: 0.00,
      isDonationBased: false,
      capacity: 0,
      tags: {},

      dateString: '',
      startTimeString: '',
      endTimeString: '',
      imageSource: new Blob(),
      isFree: false,
      noCapacity: false,

      photoUrl: '',
      imageEdited: false
    }
  },
  computed: {
    validation () {
      const validations = {
        title: this.title,
        date: this.dateString >= this.today(),
        startTime: this.dateString + this.startTimeString >= this.today() + this.earliestTime(),
        endTime: this.endTimeString > this.startTimeString,
        venue: this.venue
      }
      return {
        title: {
          wasValidated: this.wasValidated,
          invalid: !validations.title,
          feedback: validations.title ? '' : 'Please provide an event title'
        },
        date: {
          wasValidated: this.wasValidated,
          invalid: !validations.date,
          feedback: validations.date ? '' : 'Please enter a date on or after today'
        },
        startTime: {
          wasValidated: this.wasValidated,
          invalid: !validations.startTime,
          feedback: validations.startTime ? '' : 'Please enter a time in the future'
        },
        endTime: {
          wasValidated: this.wasValidated,
          invalid: !validations.endTime,
          feedback: validations.endTime ? '' : 'Please enter an end time that\'s later than the start time'
        },
        venue: {
          wasValidated: this.wasValidated,
          invalid: !validations.venue,
          feedback: validations.venue ? '' : 'Please give the venue of the event'
        }
      }
    }
  },
  methods: {
    setDateAndTime () {
      this.dateString = this.today()
      this.startTimeString = this.earliestTime()
    },
    setDescription (desc) {
      this.description = desc
    },
    setHtmlDescription (desc) {
      this.htmlDescription = desc
    },
    setTags (e) {
      const tagList = window.$(e.target).val()
      const newTags = {}
      tagList.forEach(tag => { newTags[tag] = true })
      this.tags = Object.assign({}, newTags)
    },
    extractPrefill () {
      if (this.isNew) {
        this.initialTitle = 'Add an Event'
        return
      }

      for (const field in this.prefill) {
        const sharedField = this.hasOwnProperty(field) && this.prefill.hasOwnProperty(field)
        if (!sharedField) continue

        this[field] = this.prefill[field]
      }

      this.initialTitle = this.prefill.title
      this.dateString = makeFormDateString(this.prefill.date)
      this.startTimeString = makeFomTimeString(this.prefill.date)
      this.endTimeString = makeFomTimeString(this.prefill.endDate)
      getBlobFromUrl(this.prefill.photoUrl)
        .then(blob => {
          this.imageSource = blob
          this.photoUrl = this.prefill.photoUrl
        })

      this.isDonationBased = this.prefill.donation || false
      this.isFree = (this.prefill.price || 1) === 0
      this.noCapacity = !this.prefill.capacity
    },
    handleSubmit (e) {
      this.wasValidated = true
      if (!e.target.checkValidity()) {
        let invalidElt = document.querySelector('input:invalid')
        if (invalidElt.id !== 'title' && !this.description.trim()) {
          // We want to scroll to the highest invalid field in the table.
          // Easy, except the description field is handled by Quill, which
          // renders a <div> instead of an <input>. So if the input:invalid
          // selector gives something lower than description (i.e., not 'title')
          // but the description is also invalid, scroll to description instead of
          // whatever invalid field is below description.
          invalidElt = this.$refs.quill.$el
        }
        invalidElt.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
        return
      }

      const eventData = {
        id: this.id,
        title: this.title,
        description: this.description,
        date: makeBackendDateString(this.dateString, this.startTimeString),
        venue: this.venue,
        address: this.address,
        city: this.city,
        price: this.price,
        donation: this.isDonationBased,
        capacity: this.capacity,
        tags: this.tags
      }
      eventData['html_description'] = this.htmlDescription
      eventData['end_date'] = makeBackendDateString(this.dateString, this.endTimeString)
      eventData['zip_code'] = this.zipCode
      if (this.imageEdited) {
        eventData['image_file'] = this.imageSource
      } else {
        eventData['photo_url'] = this.photoUrl
      }
      if (this.isDonationBased) {
        eventData['min_donation'] = this.price
      }
      this.postEvent(eventData)
        .then(response => {
          this.$emit('result', response)
        })
        .catch(error => {
          this.$emit('result', error)
        })
    }
  },
  created () {
    this.setDateAndTime()
    this.extractPrefill()
  },
  watch: {
    isFree (newVal) {
      if (newVal) {
        this.isDonationBased = false
        this.price = 0
      }
    },
    isDonationBased (newVal) {
      if (newVal) {
        this.isFree = false
      }
    },
    noCapacity (newVal) {
      if (newVal) {
        this.capacity = 0
      }
    },
    tags (newVal) {
      const options = this.$refs.tags.options
      Array.prototype.forEach.call(options, (opt, i) => {
        options[i].selected = newVal[opt.value] === true
      })
    },
    imageSource (newVal, oldVal) {
      if (this.isNew || oldVal.size > 0) {
        this.imageEdited = true
        this.photoUrl = ''
      }
    }
  }
}

function today () {
  return makeFormDateString(new Date())
}

function earliestTime () {
  return makeFomTimeString(new Date())
}

function makeFormDateString (dateObj) {
  if (!dateObj) return ''

  const [year, date] = [dateObj.getFullYear(), String(dateObj.getDate()).padStart(2, '0')]
  const month = String(dateObj.getMonth() + 1).padStart(2, '0')
  return [year, month, date].join('-')
}

function makeFomTimeString (dateObj) {
  if (!dateObj) return ''

  return [dateObj.getHours(), dateObj.getMinutes()]
    .map(piece => String(piece).padStart(2, '0'))
    .join(':')
}

function makeBackendDateString (dateString, timeString) {
  const [year, month, day] = dateString.split('-')
  const [hr, min] = timeString.split(':')
  return [year.slice(2), month, day, hr, min].join('')
}

function getBlobFromUrl (url) {
  return new Promise(resolve => {
    if (!url) {
      resolve(new Blob())
    } else {
      const img = new Image()
      img.setAttribute('crossOrigin', 'anonymous')
      img.src = url
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height

        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0)
        canvas.toBlob(resolve)
      }
    }
  })
}

</script>
